// src/screens/DealerCrmSettingsScreen.js
// Dealer-admin settings for CRM lead delivery (ADF/XML by email).
// Rendered as a tab inside DealerDashboardScreen.
import React, { useEffect, useState } from 'react';
import { dealerAPI } from '../services/api';

const TEMPLATE_VARS = ['{{customerName}}', '{{vehicleYear}}', '{{vehicleMake}}', '{{vehicleModel}}', '{{dealerName}}'];

const Toggle = ({ checked, onChange, label, description, disabled }) => (
  <label className={`flex items-start gap-3 ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors mt-0.5 ${
        checked ? 'bg-app-accent' : 'bg-app-border-light dark:bg-app-border'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform mt-0.5 ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
    <span>
      <span className="block text-sm font-semibold text-app-text-light dark:text-app-text">{label}</span>
      {description && (
        <span className="block text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-0.5">
          {description}
        </span>
      )}
    </span>
  </label>
);

const DealerCrmSettingsScreen = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [enabled, setEnabled] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [importEmail, setImportEmail] = useState('');
  const [payloadPlacement, setPayloadPlacement] = useState('body');
  const [subjectTemplate, setSubjectTemplate] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await dealerAPI.getCrmConfig();
        setEnabled(!!data.enabled);
        setTestMode(!!data.testMode);
        setImportEmail(data.importEmail || '');
        setPayloadPlacement(data.payloadPlacement || 'body');
        setSubjectTemplate(data.subjectTemplate || '');
      } catch (e) {
        setError(e.response?.data?.message || 'Failed to load CRM settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (enabled && !importEmail.trim()) {
      setError('Enter your CRM lead-import email address before enabling delivery.');
      return;
    }

    setSaving(true);
    try {
      const { data } = await dealerAPI.updateCrmConfig({
        importEmail: importEmail.trim(),
        payloadPlacement,
        subjectTemplate: subjectTemplate.trim(),
        enabled,
        testMode,
      });
      setEnabled(!!data.enabled);
      setTestMode(!!data.testMode);
      setImportEmail(data.importEmail || '');
      setPayloadPlacement(data.payloadPlacement || 'body');
      setSubjectTemplate(data.subjectTemplate || '');
      setSuccess('CRM settings saved.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save CRM settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-app-surface rounded-lg shadow-sm border border-app-border-light dark:border-app-border p-8 text-center text-app-text-secondary-light dark:text-app-text-secondary">
        Loading CRM settings…
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-4">
      {/* Explainer */}
      <div className="bg-white dark:bg-app-surface rounded-lg shadow-sm border border-app-border-light dark:border-app-border p-6">
        <h2 className="text-lg font-bold text-app-text-light dark:text-app-text mb-2">CRM Lead Delivery</h2>
        <p className="text-sm text-app-text-secondary-light dark:text-app-text-secondary">
          When a customer <strong>checks in</strong> for their test drive — meaning they've physically
          arrived at your dealership — SelfDrive automatically sends a lead to your CRM in the
          industry-standard <strong>ADF/XML</strong> format. It includes the customer's contact details, the
          vehicle they're test driving, and the check-in time. Virtually every dealer CRM
          (VinSolutions, eLead, DealerSocket, and others) accepts ADF leads by email.
        </p>
      </div>

      {/* Settings form */}
      <form onSubmit={handleSave} className="bg-white dark:bg-app-surface rounded-lg shadow-sm border border-app-border-light dark:border-app-border p-6 space-y-5">
        {error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 text-sm">
            {success}
          </div>
        )}

        <Toggle
          checked={enabled}
          onChange={setEnabled}
          label="Enable CRM lead delivery"
          description="Send a lead to your CRM every time a customer checks in for a test drive."
        />

        <div>
          <label className="block text-sm font-semibold text-app-text-light dark:text-app-text mb-1">
            CRM lead-import email address
          </label>
          <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mb-2">
            The email address your CRM provides for importing leads. Find it in your CRM's lead-source
            or ADF-import settings, or ask your CRM administrator.
          </p>
          <input
            type="email"
            value={importEmail}
            onChange={(e) => setImportEmail(e.target.value)}
            placeholder="leads@yourdealership.yourcrm.com"
            className="w-full px-3 py-2 rounded-lg border border-app-border-light dark:border-app-border bg-app-bg-light dark:bg-app-bg text-sm text-app-text-light dark:text-app-text focus:outline-none focus:ring-2 focus:ring-app-accent"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-app-text-light dark:text-app-text mb-1">
            Lead format placement
          </label>
          <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mb-2">
            Most CRMs read the ADF/XML from the email body. Switch to attachment only if your CRM
            requires it.
          </p>
          <select
            value={payloadPlacement}
            onChange={(e) => setPayloadPlacement(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-app-border-light dark:border-app-border bg-app-bg-light dark:bg-app-bg text-sm text-app-text-light dark:text-app-text focus:outline-none focus:ring-2 focus:ring-app-accent"
          >
            <option value="body">Email body (recommended)</option>
            <option value="attachment">XML attachment (lead.xml)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-app-text-light dark:text-app-text mb-1">
            Email subject template
          </label>
          <input
            type="text"
            value={subjectTemplate}
            onChange={(e) => setSubjectTemplate(e.target.value)}
            placeholder="New Test Drive Lead: {{customerName}} - {{vehicleYear}} {{vehicleMake}} {{vehicleModel}}"
            className="w-full px-3 py-2 rounded-lg border border-app-border-light dark:border-app-border bg-app-bg-light dark:bg-app-bg text-sm text-app-text-light dark:text-app-text focus:outline-none focus:ring-2 focus:ring-app-accent"
          />
          <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
            Available variables: {TEMPLATE_VARS.join(', ')}
          </p>
        </div>

        <div className="pt-2 border-t border-app-border-light/50 dark:border-app-border">
          <Toggle
            checked={testMode}
            onChange={setTestMode}
            label="Test mode"
            description="Build and record each lead without emailing your CRM. Use this to verify the setup — SelfDrive support can review the generated leads with you — then turn it off to go live."
          />
        </div>

        <button
          type="submit"
          disabled={saving}
          className="px-6 py-2.5 rounded-lg bg-app-accent hover:bg-app-accent-hover text-white text-sm font-semibold transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save CRM Settings'}
        </button>
      </form>

      {/* Status hint */}
      {enabled && testMode && (
        <div className="p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-sm text-yellow-800 dark:text-yellow-300">
          Test mode is on: leads are generated and recorded but <strong>not</strong> emailed to your CRM.
        </div>
      )}
      {enabled && !testMode && (
        <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-800 dark:text-green-300">
          Live: every test-drive check-in sends a lead to <strong>{importEmail}</strong>.
        </div>
      )}
    </div>
  );
};

export default DealerCrmSettingsScreen;
